import flatten from "lodash/flatten";
import type {
  EventIncidentScoreType,
  EventIncidentType,
  EventType,
  RunnerAggregatedPriceType,
} from "../types/eventType";
import {
  IncidentTypeStates,
  liveSportsNoScore,
  MarketPeriods,
  MarketTypes,
  onextwoSports,
  RunnerTypes,
  SportsNameType,
  TabTypes,
} from "../utils/const";
export class EventHelper {
  // an event is live if `allowLiveBetting` and `inRunning` are true
  static isEventLive(event: EventType): boolean {
    return event["allowLiveBetting"] && event["inRunning"];
  }

  static isEventLiveCricket(event: EventType): boolean | null {
    return (
      (event.sportType === SportsNameType.CRICKET &&
        event.eventPhase?.includes("INNINGS")) ||
      null
    );
  }
  static isEventInExtraTime(event: EventType): boolean {
    if (event["eventIncidents"] === undefined) return false;

    if (
      event["eventIncidents"][0]["eventPhase"].includes(
        MarketPeriods.BEFORE_EXTRA_TIME
      )
    ) {
      return false;
    } else if (
      event["eventIncidents"][0]["eventPhase"].includes(
        MarketPeriods.EXTRA_TIME
      )
    ) {
      return true;
    } else return false;
  }

  static isEventIncidentPhaseExtraTime(eventIncidents: any): boolean {
    if (eventIncidents === undefined) return false;

    if (
      eventIncidents[0]["eventPhase"].includes(MarketPeriods.BEFORE_EXTRA_TIME)
    ) {
      return false;
    } else if (
      eventIncidents[0]["eventPhase"].includes(MarketPeriods.EXTRA_TIME)
    ) {
      return true;
    } else return false;
  }

  static isBeforeExtraTime(event: EventType): boolean {
    if (event["eventIncidents"] === undefined) return false;

    if (
      event["eventIncidents"][0]["eventPhase"].includes(
        MarketPeriods.BEFORE_EXTRA_TIME
      )
    ) {
      return true;
    } else return false;
  }

  static isEventPhaseOfTypeLive(event: EventType): boolean {
    if (event["eventIncidents"] == null) {
      return false;
    }

    const eventIncident = event["eventIncidents"][0];
    return eventIncident["eventPhase"] === TabTypes.LIVE;
  }

  static isEventGoingLive(event: EventType): boolean {
    return event["allowLiveBetting"];
  }

  static getScores(
    currentScores: EventIncidentScoreType[],
    showHomePlayerAsFirst: boolean
  ): string {
    let score: string = "";

    if (currentScores.length === 0) {
      return score;
    }

    const home = currentScores.filter(
      score => score.type === RunnerTypes.HOME
    )[0];
    const away = currentScores.filter(
      score => score.type === RunnerTypes.AWAY
    )[0];

    if (showHomePlayerAsFirst) {
      score =
        home != null && away != null ? home.score + " - " + away.score : "";
    } else {
      score =
        home != null && away != null ? away.score + " - " + home.score : "";
    }

    return score;
  }

  static getCornerScores(currentScores: EventIncidentScoreType[]): string {
    const home =
      currentScores &&
      currentScores.filter(
        score => score.type === IncidentTypeStates.HOME_CORNER
      )[0];
    const away =
      currentScores &&
      currentScores.filter(
        score => score.type === IncidentTypeStates.AWAY_CORNER
      )[0];
    return home != null && away != null ? home.score + " - " + away.score : "";
  }

  static getHomeScore(currentScores: EventIncidentScoreType[]): null | number {
    const home =
      currentScores &&
      currentScores.filter(score => score.type === RunnerTypes.HOME)[0];
    const away =
      currentScores &&
      currentScores.filter(score => score.type === RunnerTypes.AWAY)[0];
    return home != null && away != null ? home.score : null;
  }

  static getAwayScore(currentScores: EventIncidentScoreType[]): null | number {
    const home =
      currentScores &&
      currentScores.filter(score => score.type === RunnerTypes.HOME)[0];
    const away =
      currentScores &&
      currentScores.filter(score => score.type === RunnerTypes.AWAY)[0];
    return home != null && away != null ? away.score : null;
  }

  static getHighestOddsValueFrom(
    aggregatedPrices: RunnerAggregatedPriceType[]
  ): number {
    // BET-5572 find best odds using decimal odds (fix issue with malay and prob odds)
    let decimalOddsBestPrice = Math.max.apply(
      null,
      aggregatedPrices.map(el => el["decimalOdds"])
    );
    // using decimal odds best price get price object
    let bestPrice = aggregatedPrices.filter(
      price => price["decimalOdds"] === decimalOddsBestPrice
    );
    // return bets price display odds
    return bestPrice.length && bestPrice[0].displayOdds;
  }

  static updateActiveEvents(
    oldCompetitionList: Record<string, any>,
    newCompetitionList: Record<string, any>
  ) {
    let activeEvents = [];
    // find events that are displaying event details, ie with isActive true
    oldCompetitionList.forEach(comp => {
      comp.events.forEach(event => {
        if (event.alternativeMarketPeriods) {
          activeEvents.push({
            id: event.id,
            alternativeMarketPeriods: event.alternativeMarketPeriods,
          });
        }
      });
    });
    // Set event to show event details and add non-summarised marketPeriods to the event in the updated comp list
    newCompetitionList.forEach(comp => {
      comp.events.forEach(event => {
        const activeEvent = activeEvents.find(ae => ae.id === event.id);

        if (activeEvent) {
          event.showAlternativeLines = true;
          event.alternativeMarketPeriods = activeEvent.alternativeMarketPeriods;
        }
      });
    });
  }

  static isEventMoneyline(event: EventType): any {
    const found = flatten(
      event.marketPeriods &&
        event.marketPeriods.map(mp => mp).map(mt => mt.marketTypes)
    ).find(marketType => marketType.marketType === MarketTypes.MONEY_LINE);
    return !!found;
  }

  /**
   * Uses list of sports in const.js to deternine if event is 1x2
   * Used for determining if 'draw' runner name is to be added
   */
  static isEvent1x2(event: EventType): boolean {
    return onextwoSports.includes(event.sportType);
  }

  /**
   * Sports that can be live but live score is not available
   * Used to show "LIVE" in place of the score
   */
  static isEventLiveWithNoScore(event: EventType): boolean {
    return liveSportsNoScore.includes(event.sportType);
  }

  static formatIncidentText(eventIncident: EventIncidentType):
    | {
        dangerZoneState: string;
        incidentType: string;
        isAway: boolean;
        isHome: boolean;
        possibleVar: boolean;
      }
    | {
        dangerZoneState: string;
        incidentType: null | string;
        isAway: boolean;
        isHome: boolean;
        possibleVar: boolean;
      } {
    if (!eventIncident) {
      return {
        isHome: false,
        isAway: false,
        possibleVar: false,
        incidentType: "",
        dangerZoneState: "",
      };
    }

    let isHome;
    let isAway;
    let incidentType: any = null;
    let possibleVar = false;
    const dangerZoneState =
      eventIncident != null ? eventIncident["dangerballStatus"] : "";
    // check if incident type should override danger status
    if (eventIncident.type != null) {
      if (
        eventIncident.type.includes("GOAL") ||
        eventIncident.type.includes("CORNER") ||
        eventIncident.type.includes("YELLOW") ||
        eventIncident.type.includes("RED")
      ) {
        incidentType = eventIncident.type;
      }
    }

    // BET-5610 incident type available give precedence
    if (incidentType === null) {
      isHome = dangerZoneState && dangerZoneState.includes(RunnerTypes.HOME);
      isAway = dangerZoneState && dangerZoneState.includes(RunnerTypes.AWAY);
    } else {
      isHome = incidentType.includes(RunnerTypes.HOME);
      isAway = incidentType.includes(RunnerTypes.AWAY);
    }

    if (
      incidentType === null &&
      dangerZoneState &&
      dangerZoneState.includes("POSSIBLE")
    ) {
      // BET-5712 possible var event incident has no particapant id so no side is returned from API
      possibleVar = true;
    }

    return {
      isHome,
      isAway,
      possibleVar,
      incidentType,
      dangerZoneState,
    };
  }
}
